/**
 * GenericPromptWorkflow Component
 * 
 * Specialized workflow for Dimensions AI QC pipeline with Aceternity animations
 * and Tremor data display patterns. Features read-only prompt configuration
 * optimized for generic specification extraction and validation.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { globalPromptsAPI } from '../../services/api';
import {
  Sparkles,
  Home,
  ChevronRight,
  Upload,
  Settings,
  CheckCircle2,
  FileSpreadsheet,
  Loader2,
  AlertCircle,
  X,
  Download,
  FolderOpen,
  Zap,
  Lock,
  DollarSign,
  Calculator,
  FileText,
  FileOutput,
  Clock,
  Play,
  AlertTriangle,
  XCircle,
} from 'lucide-react';
import { theme } from '../../styles/theme';
import { filesAPI, jobsAPI } from '../../services/api';
import type { FileUpload } from '../../types';
import { FilePreviewDialog } from '../../components/FilePreviewDialog';
import { CostConfirmation } from '../../components/CostConfirmation';
import { extractApiError } from '../../utils/errorMessages';
import ModelSelect from '../../components/ModelSelect';

interface PipelineConfig {
  pipeline_type: string;
  name: string;
  description: string;
  default_prompt: string;
  default_model: string;
  default_chunk_size: number;
  default_temperature: number;
  default_max_concurrent: number;
  required_columns: string[];
  features: string[];
}

interface CostEstimate {
  estimated_cost: {
    usd: number;
    sgd: number;
  };
  estimated_tokens: {
    total_input_tokens: number;
    total_output_tokens: number;
    grand_total_tokens: number;
  };
  processing_time_minutes?: number;
}

interface ColumnInfo {
  name: string;
  unique_count: number;
  null_count: number;
  uniqueness_ratio: number;
  is_suitable: boolean;
  sample_values: (string | null)[];
}

interface ColumnAnalysisResult {
  file_id: string;
  filename: string;
  total_rows: number;
  total_columns: number;
  columns: ColumnInfo[];
  suggested_join_column: string | null;
  suggestion_reason: string;
}

const GenericPromptWorkflow: React.FC = () => {
  const navigate = useNavigate();
  
  // State management
  const [currentStep, setCurrentStep] = useState<0 | 1>(0);
  const [uploadedFiles, setUploadedFiles] = useState<FileUpload[]>([]);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  // File preview state
  const [filePreview, setFilePreview] = useState<string[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  
  // Column analysis state
  const [columnAnalysis, setColumnAnalysis] = useState<ColumnAnalysisResult | null>(null);
  const [analyzingColumns, setAnalyzingColumns] = useState(false);
  const [selectedJoinColumn, setSelectedJoinColumn] = useState<string>('');
  
  // Configuration state
  const [pipelineConfig, setPipelineConfig] = useState<PipelineConfig | null>(null);
  const [allPrompts, setAllPrompts] = useState<PipelineConfig[]>([]);
  const [selectedPromptId, setSelectedPromptId] = useState<string>('custom');
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState('gemini-2.5-flash');
  const [chunkSize, setChunkSize] = useState(20);
  const [maxConcurrent, setMaxConcurrent] = useState(5);
  const [temperature, setTemperature] = useState(0.7);
  const [projectName, setProjectName] = useState('');
  const [estimatedCost, setEstimatedCost] = useState<CostEstimate | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState('');
  const [costConfirmed, setCostConfirmed] = useState(false);
  // The backend rejects a start without an Idempotency-Key (UUID v4). Held in a
  // ref so a double-click replays the same job instead of creating (and paying
  // for) a second one; rotated only after a start is accepted.
  const idempotencyKeyRef = React.useRef<string>(crypto.randomUUID());
  
  // Validation errors
  const [chunkSizeError, setChunkSizeError] = useState<string>('');
  const [maxConcurrentError, setMaxConcurrentError] = useState<string>('');
  
  // Validation helper function
  const validatePositiveInteger = (
    value: string | number,
    min: number,
    max: number,
    fieldName: string
  ): string => {
    const strValue = String(value).trim();
    
    if (!strValue || strValue === '') {
      return `${fieldName} is required`;
    }
    
    if (!/^\d+$/.test(strValue)) {
      return `${fieldName} must be a positive integer`;
    }
    
    const num = Number(strValue);
    
    if (num < min || num > max) {
      return `${fieldName} must be between ${min} and ${max}`;
    }
    
    return '';
  };
  
  // Fetch pipeline configuration on mount
  React.useEffect(() => {
    fetchPipelineConfig();
  }, []);
  
  const fetchPipelineConfig = async () => {
    try {
      // Fetch ALL prompts to populate dropdown
      const response = await globalPromptsAPI.list();
      const allData = Array.isArray(response.data) ? response.data : response.data.results || [];
      
      // Filter to only show pipeline-specific prompts
      const pipelinePrompts = allData.filter((p: PipelineConfig) => 
        ['dimensions', 'material', 'part_category', 'generic'].includes(p.pipeline_type)
      );
      setAllPrompts(pipelinePrompts);
      
      // Set generic as default
      const genericConfig = pipelinePrompts.find((p: PipelineConfig) => p.pipeline_type === 'generic');
      if (genericConfig) {
        setPipelineConfig(genericConfig);
        setCustomPrompt(genericConfig.default_prompt);
        setSelectedModel(genericConfig.default_model);
        setChunkSize(genericConfig.default_chunk_size || 20);
        setMaxConcurrent(5);
        setTemperature(genericConfig.default_temperature || 0.7);
      }
    } catch (error) {
      console.error('Failed to fetch pipeline config:', error);
    }
  };
  
  const handlePromptSelection = async (promptId: string) => {
    console.log('Prompt selection changed to:', promptId);
    setSelectedPromptId(promptId);
    
    if (promptId === 'custom') {
      // Keep current custom prompt
      console.log('Custom prompt selected, keeping current prompt');
      return;
    }
    
    // Fetch the selected prompt from API
    console.log('Fetching prompt from API for:', promptId);
    try {
      const response = await globalPromptsAPI.getByPipelineType(promptId);
      console.log('API response:', response.data);
      
      // Find the prompt with matching pipeline_type
      const allResults = Array.isArray(response.data) ? response.data : response.data.results || [];
      const selectedPrompt = allResults.find((p: PipelineConfig) => p.pipeline_type === promptId);
      
      if (selectedPrompt) {
        console.log('Updating prompt to:', selectedPrompt.name);
        setCustomPrompt(selectedPrompt.default_prompt);
        setSelectedModel(selectedPrompt.default_model);
        setChunkSize(selectedPrompt.default_chunk_size || 20);
      } else {
        console.warn('No prompt found with pipeline_type:', promptId);
      }
    } catch (error) {
      console.error('Failed to fetch selected prompt:', error);
    }
  };
  
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      await handleFileUpload(files);
    }
  };
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const files = Array.from(e.target.files);
      await handleFileUpload(files);
    }
  };
  
  const handleFileUpload = async (files: File[]) => {
    setUploading(true);
    
    for (const file of files) {
      try {
        const response = await filesAPI.upload(file);
        setUploadedFiles(prev => [...prev, response.data]);
        
        // Always select and preview the latest uploaded file
        setSelectedFileId(response.data.id);
        // Generate preview for the uploaded file
        await generateFilePreview(file);
        // Analyze columns for join column suggestion
        await analyzeColumns(response.data.id);
      } catch (error) {
        console.error('Upload failed:', error);
      }
    }
    
    setUploading(false);
  };
  
  /**
   * Analyze file columns to suggest best join column
   * Calls backend API to get uniqueness ratios for each column
   */
  const analyzeColumns = async (fileId: string) => {
    setAnalyzingColumns(true);
    
    try {
      const response = await jobsAPI.analyzeColumns({ file_id: fileId });
      setColumnAnalysis(response.data);
      
      // Auto-select suggested join column if available
      if (response.data.suggested_join_column) {
        setSelectedJoinColumn(response.data.suggested_join_column);
      }
    } catch (error) {
      console.error('Column analysis failed:', error);
      setColumnAnalysis(null);
    } finally {
      setAnalyzingColumns(false);
    }
  };
  
  /**
   * Generate a preview of the file content (first 100 lines)
   * Supports both CSV and Excel files
   */
  const generateFilePreview = async (file: File) => {
    setLoadingPreview(true);
    setPreviewError('');
    
    try {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      
      if (fileExtension === 'csv') {
        // Handle CSV files with FileReader
        const text = await file.text();
        const lines = text.split('\n').slice(0, 100);
        setFilePreview(lines);
      } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        // Handle Excel files with SheetJS (xlsx)
        const XLSX = await import('xlsx');
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        
        // Read first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to CSV format for preview
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const lines = csv.split('\n').slice(0, 100);
        setFilePreview(lines);
      } else {
        setPreviewError('Unsupported file format. Please upload a CSV or Excel file.');
      }
    } catch (error) {
      console.error('Failed to generate preview:', error);
      setPreviewError('Failed to read file content. Please ensure the file is not corrupted.');
    } finally {
      setLoadingPreview(false);
    }
  };
  
  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    if (selectedFileId === fileId) {
      setSelectedFileId(uploadedFiles[0]?.id || null);
    }
  };
  
  const handleEstimateCost = async () => {
    if (!selectedFileId || !pipelineConfig) return;
    
    setEstimating(true);
    try {
      const response = await jobsAPI.estimate({
        file_id: selectedFileId,
        pipeline_type: 'generic',
        prompt_text: customPrompt,
        model_name: selectedModel,
        chunk_size: chunkSize,
        max_concurrent: maxConcurrent,
        temperature: pipelineConfig?.default_temperature || 0.1,
        output_multiplier: 1.8,  // Default multiplier for estimating output tokens
      });
      setEstimatedCost(response.data);
    } catch (error) {
      console.error('Cost estimation failed:', error);
    } finally {
      setEstimating(false);
    }
  };
  
  const handleStartProcessing = async () => {
    if (!selectedFileId || !pipelineConfig) return;
    
    setStarting(true);
    setStartError('');
    try {
      // Generate unique job name with project name and timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const jobName = projectName ? `${projectName}-${timestamp}` : `Generic-${timestamp}`;
      
      const response = await jobsAPI.start({
        file_id: selectedFileId,
        pipeline_type: 'generic',
        project_name: projectName || '',
        job_name: jobName,
        prompt_text: customPrompt,
        model_name: selectedModel,
        chunk_size: chunkSize,
        max_concurrent: maxConcurrent,
        temperature: temperature,
        join_column: selectedJoinColumn || undefined,  // Pass join column to backend
      }, idempotencyKeyRef.current);
      idempotencyKeyRef.current = crypto.randomUUID();

      // Navigate to job detail page
      navigate(`/jobs/${response.data.id}`);
    } catch (error) {
      // A rejected start used to only reach console.error, so the button simply
      // re-enabled and the user was given no reason (CLAUDE.md §5).
      console.error('Failed to start job:', error);
      setStartError(
        extractApiError(error, 'Could not start the job. Please try again.')
      );
      setStarting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-bg-base">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm text-text-tertiary mb-6" aria-label="Breadcrumb">
        <Link to="/dashboard" className="flex items-center hover:text-text-primary transition-colors">
          <Home className="w-4 h-4" />
        </Link>
        <ChevronRight className="w-4 h-4 mx-2" />
        <Link to="/select-pipeline" className="hover:text-text-primary transition-colors">
          Select Pipeline
        </Link>
        <ChevronRight className="w-4 h-4 mx-2" />
        <span className="text-text-primary font-medium" aria-current="page">Generic Prompt QC</span>
      </nav>

      {/* Progress Stepper */}
      <div className="flex items-center justify-between mb-8" role="progressbar" aria-valuenow={currentStep} aria-valuemin={0} aria-valuemax={1}>
        {/* Step 1: Upload Files */}
        <div className="flex items-center flex-1">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
              currentStep >= 0
                ? 'bg-interactive-primary text-text-inverse shadow-lg'
                : 'bg-bg-surface border-2 border-border text-text-tertiary'
            }`}
            data-testid="workflow-step-0"
          >
            {currentStep > 0 ? <CheckCircle2 className="w-5 h-5" /> : '1'}
          </div>
          <span className={`ml-3 font-medium ${currentStep >= 0 ? theme.text.primary : theme.text.tertiary}`}>
            Upload Files
          </span>
        </div>
        
        {/* Connector */}
        <div className="flex-1 h-0.5 bg-border-default mx-4"></div>
        
        {/* Step 2: Configure & Process */}
        <div className="flex items-center flex-1 justify-end">
          <span className={`mr-3 font-medium ${currentStep >= 1 ? theme.text.primary : theme.text.tertiary}`}>
            Configure & Process
          </span>
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
              currentStep >= 1
                ? 'bg-interactive-primary text-text-inverse shadow-lg'
                : 'bg-bg-surface border-2 border-border text-text-tertiary'
            }`}
            data-testid="workflow-step-1"
          >
            2
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-bg-surface border border-border rounded-lg p-1.5 inline-flex gap-1 mb-8">
        <button
          onClick={() => setCurrentStep(0)}
          className={`px-6 py-3 rounded-md font-medium transition-all duration-300 flex items-center gap-2 ${
            currentStep === 0
              ? 'bg-bg-surface text-primary shadow-sm'
              : 'text-text-secondary hover:text-text-primary hover:bg-bg-base/50'
          }`}
          data-testid="upload-tab"
        >
          <Upload className="w-4 h-4" />
          Upload
        </button>
        <button
          onClick={() => setCurrentStep(1)}
          disabled={!selectedFileId}
          className={`px-6 py-3 rounded-md font-medium transition-all duration-300 flex items-center gap-2 ${
            currentStep === 1
              ? 'bg-bg-surface text-primary shadow-sm'
              : 'text-text-secondary hover:text-text-primary hover:bg-bg-base/50 disabled:opacity-50 disabled:cursor-not-allowed'
          }`}
          data-testid="configure-tab"
        >
          <Settings className="w-4 h-4" />
          Configure
        </button>
      </div>

      {/* Tab Content */}
      <div className="pt-8 min-h-[500px]">
        {currentStep === 0 && (
          <div data-testid="upload-zone">
            {/* Upload Zone */}
            <div
              data-testid="file-dropzone"
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`relative group border-2 border-dashed rounded-2xl bg-bg-surface/50 backdrop-blur-sm p-12 text-center transition-all duration-300 ${
                dragActive
                  ? 'border-primary bg-primary/5 scale-[1.02]'
                  : 'border-border hover:border-primary hover:bg-primary/5'
              }`}
            >
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-bg-subtle flex items-center justify-center">
                <Upload className="w-16 h-16 text-text-tertiary" />
              </div>
              <h3 className={`text-xl font-semibold ${theme.text.primary} mb-2`}>
                Drop your files here
              </h3>
              <p className={`text-sm ${theme.text.secondary} mb-4`}>
                or click to browse
              </p>
              <div className="flex gap-2 justify-center mb-4">
                <span className="px-3 py-1 bg-bg-surface border border-border rounded-full text-xs">
                  .xlsx
                </span>
                <span className="px-3 py-1 bg-bg-surface border border-border rounded-full text-xs">
                  .xls
                </span>
                <span className="px-3 py-1 bg-bg-surface border border-border rounded-full text-xs">
                  .csv
                </span>
              </div>
              <label className="inline-flex items-center gap-2 text-primary hover:underline cursor-pointer">
                <FolderOpen className="w-4 h-4" />
                Browse files
                <input
                  data-testid="file-input"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                  multiple
                  className="hidden"
                />
              </label>
            </div>

            {/* Download Template Button */}
            <div className="mt-6 text-center">
              <button
                className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-bg-surface transition-colors"
                onClick={() => window.open('/api/v1/data-utilities/pipeline-template/?type=generic', '_blank')}
              >
                <Download className="w-4 h-4" />
                Download Example Template for Generic Prompt QC
              </button>
            </div>

            {/* File Preview Cards */}
            {uploadedFiles.length > 0 && (
              <div className="grid gap-4 mt-6">
                {uploadedFiles.map((file) => (
                  <div
                    key={file.id}
                    className={`bg-bg-surface/80 backdrop-blur border rounded-xl p-4 flex items-center justify-between transition-all duration-200 ${
                      selectedFileId === file.id
                        ? 'border-primary shadow-lg'
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => setSelectedFileId(file.id)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="flex items-center gap-4">
                      <FileSpreadsheet className="w-10 h-10 text-primary" />
                      <div>
                        <p className={`font-medium ${theme.text.primary} truncate max-w-md`}>
                          {file.original_filename}
                        </p>
                        <p className={`text-sm ${theme.text.secondary}`}>
                          {(file.file_size / 1024).toFixed(2)} KB • {file.row_count} rows
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="px-3 py-1 bg-qc-success/10 text-qc-success rounded-full text-xs flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Complete
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(file.id);
                        }}
                        className="p-2 hover:bg-qc-error/20 rounded-full transition-colors"
                        aria-label="Remove file"
                      >
                        <X className="w-4 h-4 text-qc-error" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* File Content Preview - Button */}
            {uploadedFiles.length > 0 && filePreview.length > 0 && (
              <div className="mt-6">
                <button
                  onClick={() => setShowPreview(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-bg-surface hover:bg-bg-base border border-border rounded-lg transition-colors"
                >
                  <FileText className="w-4 h-4 text-primary" />
                  <span className={`text-sm font-medium ${theme.text.primary}`}>
                    Show File Preview
                  </span>
                  <span className={`text-xs ${theme.text.secondary}`}>
                    ({filePreview.length} rows)
                  </span>
                </button>
              </div>
            )}

            {/* File Preview Dialog (Modal) */}
            <FilePreviewDialog
              open={showPreview}
              filePreview={filePreview}
              onClose={() => setShowPreview(false)}
            />

            {/* Column Analysis Section */}
            {uploadedFiles.length > 0 && columnAnalysis && (
              <div className="mt-6 bg-bg-surface border border-border rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-primary" />
                    <h3 className={`text-lg font-semibold ${theme.text.primary}`}>
                      Join Column Detection
                    </h3>
                  </div>
                  {analyzingColumns && (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      <span className={`text-sm ${theme.text.secondary}`}>
                        Analyzing...
                      </span>
                    </div>
                  )}
                </div>

                {/* Suggestion Banner */}
                {columnAnalysis.suggested_join_column && (
                  <div className="mb-4 p-4 bg-qc-success/10 border border-qc-success/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-qc-success flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-qc-success mb-1">
                          Recommended Join Column
                        </p>
                        <p className="text-sm text-qc-success">
                          {columnAnalysis.suggestion_reason}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Join Column Dropdown */}
                <div className="mb-4">
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Select Join Column
                    <span className="ml-1 text-text-tertiary" aria-hidden="true">*</span>
                  </label>
                  <select
                    value={selectedJoinColumn}
                    onChange={(e) => setSelectedJoinColumn(e.target.value)}
                    className={`w-full px-4 py-2 bg-bg-base border border-border rounded-lg ${theme.text.primary} focus:outline-none focus:ring-2 focus:ring-primary`}
                  >
                    <option value="">-- Select a column --</option>
                    {columnAnalysis.columns
                      .sort((a, b) => b.uniqueness_ratio - a.uniqueness_ratio)
                      .map((col) => (
                        <option key={col.name} value={col.name}>
                          {col.name} ({(col.uniqueness_ratio * 100).toFixed(1)}% unique
                          {col.is_suitable ? ' ✓' : ''})
                        </option>
                      ))}
                  </select>
                  <p className={`mt-1 text-xs ${theme.text.secondary}`}>
                    💡 Choose a column with high uniqueness (ideally 100%) and no null values
                  </p>
                </div>

                {/* Column Details Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className={`text-left py-2 px-3 ${theme.text.secondary} font-medium`}>
                          Column
                        </th>
                        <th className={`text-right py-2 px-3 ${theme.text.secondary} font-medium`}>
                          Unique
                        </th>
                        <th className={`text-right py-2 px-3 ${theme.text.secondary} font-medium`}>
                          Nulls
                        </th>
                        <th className={`text-right py-2 px-3 ${theme.text.secondary} font-medium`}>
                          Uniqueness
                        </th>
                        <th className={`text-left py-2 px-3 ${theme.text.secondary} font-medium`}>
                          Sample Values
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {columnAnalysis.columns
                        .sort((a, b) => b.uniqueness_ratio - a.uniqueness_ratio)
                        .map((col) => (
                          <tr
                            key={col.name}
                            className={`border-b border-border/50 hover:bg-bg-base transition-colors ${
                              col.name === selectedJoinColumn ? 'bg-primary/10' : ''
                            }`}
                          >
                            <td className={`py-2 px-3 font-medium ${theme.text.primary}`}>
                              {col.name}
                              {col.is_suitable && (
                                <span className="ml-2 text-qc-success">✓</span>
                              )}
                            </td>
                            <td className={`py-2 px-3 text-right ${theme.text.primary}`}>
                              {col.unique_count.toLocaleString()}
                            </td>
                            <td className={`py-2 px-3 text-right ${col.null_count > 0 ? 'text-qc-error' : theme.text.secondary}`}>
                              {col.null_count.toLocaleString()}
                            </td>
                            <td className={`py-2 px-3 text-right ${theme.text.primary}`}>
                              <span className={`px-2 py-1 rounded ${
                                col.uniqueness_ratio >= 0.95
                                  ? 'bg-qc-success/10 text-qc-success'
                                  : col.uniqueness_ratio >= 0.8
                                  ? 'bg-qc-warning/10 text-qc-warning'
                                  : 'bg-qc-error/10 text-qc-error'
                              }`}>
                                {(col.uniqueness_ratio * 100).toFixed(1)}%
                              </span>
                            </td>
                            <td className={`py-2 px-3 text-xs ${theme.text.secondary} font-mono`}>
                              {col.sample_values?.slice(0, 3).join(', ') || 'N/A'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                <div className={`mt-4 text-xs ${theme.text.tertiary}`}>
                  📊 Showing {columnAnalysis.columns?.length ?? 0} columns from {(columnAnalysis.total_rows ?? 0).toLocaleString()} rows
                </div>
              </div>
            )}

            {/* Continue Button */}
            {uploadedFiles.length > 0 && (
              <div className="mt-8 flex justify-end">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-6 py-3 bg-interactive-primary text-text-inverse rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center gap-2"
                >
                  Continue to Configuration
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {currentStep === 1 && pipelineConfig && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="config-section">
            {/* Left Column: Configuration */}
            <div className="lg:col-span-2 space-y-6">
              {/* AI Prompt Section */}
              <div className="bg-bg-surface border border-border rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <h3 className={`text-lg font-semibold ${theme.text.primary}`}>
                    AI Prompt Template
                  </h3>
                </div>
                
                {/* Prompt Selection Dropdown */}
                <div className="mb-4">
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Select Prompt Template
                  </label>
                  <select
                    value={selectedPromptId}
                    onChange={(e) => handlePromptSelection(e.target.value)}
                    className={`w-full px-4 py-2 bg-bg-base border border-border rounded-lg ${theme.text.primary} focus:outline-none focus:ring-2 focus:ring-primary`}
                  >
                    <option value="custom">💡 Custom Prompt</option>
                    {allPrompts.map((prompt) => (
                      <option key={prompt.pipeline_type} value={prompt.pipeline_type}>
                        {prompt.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Editable Prompt Textarea */}
                <div className="mb-2">
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Prompt Text {selectedPromptId === 'custom' && '✨ (Editable)'}
                  </label>
                  <textarea
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    className="w-full h-64 bg-bg-base border border-border rounded-lg p-4 font-data text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Enter your custom AI prompt here..."
                  />
                </div>
                <div className="flex justify-between items-center mt-2">
                  <p className={`text-xs ${theme.text.tertiary}`}>
                    {customPrompt?.length || 0} characters
                  </p>
                  <p className={`text-xs ${theme.text.tertiary}`}>
                    💡 Fully customizable for any task
                  </p>
                </div>
              </div>

              {/* Model Settings - Unlocked for Generic */}
              <div className="bg-bg-surface border border-border rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-5 h-5 text-primary" />
                  <h3 className={`text-lg font-semibold ${theme.text.primary}`}>
                    Model Settings
                  </h3>
                </div>
                <div className="mb-2">
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    AI Model
                  </label>
                  <ModelSelect value={selectedModel} onChange={setSelectedModel} preferredDefault="gemini-2.5-flash" />
                  <p className={`text-xs ${theme.text.tertiary} mt-2`}>
                    Choose model based on complexity and budget
                  </p>
                </div>
              </div>

              {/* Processing Parameters */}
              <div className="bg-bg-surface border border-border rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Settings className="w-5 h-5 text-primary" />
                  <h3 className={`text-lg font-semibold ${theme.text.primary}`}>
                    Processing Parameters
                  </h3>
                </div>
                
                {/* Project/Organization Name Input */}
                <div className="mb-6">
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Project/Organization Name
                  </label>
                  <input
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="e.g., MyCompany, ProjectX, ClientName"
                    className={`w-full px-4 py-3 bg-bg-base border border-border rounded-lg ${theme.text.primary} focus:outline-none focus:ring-2 focus:ring-primary`}
                  />
                  <p className={`text-xs ${theme.text.tertiary} mt-2`}>
                    Job will be named: <span className="font-mono">{projectName || 'Generic'}-{new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')}</span>
                  </p>
                </div>
                
                {/* Chunk Size Input */}
                <div className="mb-6">
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Rows per chunk
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={chunkSize}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val !== '') {
                        setChunkSize(Number(val) || chunkSize);
                      }
                      const error = validatePositiveInteger(val, 10, 100, 'Rows per chunk');
                      setChunkSizeError(error);
                    }}
                    onBlur={() => {
                      const error = validatePositiveInteger(chunkSize, 10, 100, 'Rows per chunk');
                      setChunkSizeError(error);
                    }}
                    aria-invalid={!!chunkSizeError}
                    aria-describedby={chunkSizeError ? 'chunk-size-error' : undefined}
                    className={`w-full px-4 py-3 bg-bg-base border ${
                      chunkSizeError ? 'border-qc-error' : 'border-border'
                    } rounded-lg text-2xl font-data ${theme.text.primary} focus:outline-none focus:ring-2 ${
                      chunkSizeError ? 'focus:ring-qc-error' : 'focus:ring-primary'
                    } text-center`}
                  />
                  {chunkSizeError && (
                    <p id="chunk-size-error" className="text-sm text-qc-error mt-1 text-center" role="alert">
                      {chunkSizeError}
                    </p>
                  )}
                  <p className={`text-xs ${theme.text.tertiary} mt-2 text-center`}>
                    Range: 10-100 rows
                  </p>
                </div>

                {/* Max Concurrent Input */}
                <div className="mb-6">
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Max concurrent requests
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={maxConcurrent}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val !== '') {
                        setMaxConcurrent(Number(val) || maxConcurrent);
                      }
                      const error = validatePositiveInteger(val, 1, 50, 'Max concurrent requests');
                      setMaxConcurrentError(error);
                    }}
                    onBlur={() => {
                      const error = validatePositiveInteger(maxConcurrent, 1, 50, 'Max concurrent requests');
                      setMaxConcurrentError(error);
                    }}
                    aria-invalid={!!maxConcurrentError}
                    aria-describedby={maxConcurrentError ? 'max-concurrent-error' : undefined}
                    className={`w-full px-4 py-3 bg-bg-base border ${
                      maxConcurrentError ? 'border-qc-error' : 'border-border'
                    } rounded-lg text-2xl font-data ${theme.text.primary} focus:outline-none focus:ring-2 ${
                      maxConcurrentError ? 'focus:ring-qc-error' : 'focus:ring-primary'
                    } text-center`}
                  />
                  {maxConcurrentError && (
                    <p id="max-concurrent-error" className="text-sm text-qc-error mt-1 text-center" role="alert">
                      {maxConcurrentError}
                    </p>
                  )}
                  <p className={`text-xs ${theme.text.tertiary} mt-2 text-center`}>
                    Range: 1-50 requests
                  </p>
                </div>

                {/* Temperature Slider */}
                <div>
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Temperature: {temperature.toFixed(1)}
                  </label>
                  <input
                    type="range"
                    value={temperature}
                    onChange={(e) => setTemperature(parseFloat(e.target.value))}
                    min="0.1"
                    max="1.0"
                    step="0.1"
                    className="w-full h-2 bg-bg-base rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                  <div className="flex justify-between text-xs mt-2">
                    <span className={theme.text.tertiary}>0.1 (Precise)</span>
                    <span className={theme.text.tertiary}>0.5 (Balanced)</span>
                    <span className={theme.text.tertiary}>1.0 (Creative)</span>
                  </div>
                </div>

                {/* Output Token Multiplier */}
                <div>
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Estimated output token multiplier
                  </label>
                  <div className={`w-full px-4 py-3 bg-bg-base/50 border border-border rounded-lg text-2xl font-data ${theme.text.tertiary} text-center`}>
                    1.8x
                  </div>
                  <p className={`text-xs ${theme.text.tertiary} mt-2 text-center`}>
                    Multiplied by input tokens for cost estimation
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: Cost Estimation (Sticky) */}
            <div className="lg:sticky lg:top-8 h-fit" data-testid="cost-panel">
              <div className="bg-bg-subtle border-2 border-interactive-primary/20 rounded-2xl p-6">
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-primary" />
                    <h3 className={`text-lg font-semibold ${theme.text.primary}`}>
                      Cost Estimation
                    </h3>
                  </div>
                  <p className={`text-sm ${theme.text.secondary}`}>
                    Estimated processing cost
                  </p>
                </div>

                <button
                  onClick={handleEstimateCost}
                  disabled={estimating || !selectedFileId}
                  className="w-full py-3 px-4 bg-interactive-primary text-text-inverse rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mb-6"
                >
                  {estimating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Calculator className="w-4 h-4" />
                      Calculate Cost
                    </>
                  )}
                </button>

                {estimatedCost && (
                  <div className="space-y-4">
                    <div className="bg-bg-surface rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-text-tertiary" />
                        <span className={`text-sm ${theme.text.secondary}`}>
                          Input Tokens
                        </span>
                      </div>
                      <p className={`text-2xl font-data ${theme.text.primary}`}>
                        {estimatedCost.estimated_tokens.total_input_tokens.toLocaleString()}
                      </p>
                    </div>

                    <div className="bg-bg-surface rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FileOutput className="w-4 h-4 text-text-tertiary" />
                        <span className={`text-sm ${theme.text.secondary}`}>
                          Output Tokens
                        </span>
                      </div>
                      <p className={`text-2xl font-data ${theme.text.primary}`}>
                        {estimatedCost.estimated_tokens.total_output_tokens.toLocaleString()}
                      </p>
                    </div>

                    <div className="bg-bg-surface rounded-lg p-4 border-2 border-primary">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-4 h-4 text-primary" />
                        <span className={`text-sm ${theme.text.secondary}`}>
                          Total Cost
                        </span>
                      </div>
                      <p className={`text-3xl font-data ${theme.text.primary}`}>
                        ${estimatedCost.estimated_cost.usd.toFixed(4)}
                      </p>
                      <p className={`text-sm ${theme.text.secondary} mt-1`}>
                        SGD {estimatedCost.estimated_cost.sgd.toFixed(2)}
                      </p>
                    </div>

                    {estimatedCost.processing_time_minutes && (
                      <div className="bg-bg-surface rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="w-4 h-4 text-text-tertiary" />
                          <span className={`text-sm ${theme.text.secondary}`}>
                            Est. Processing Time
                          </span>
                        </div>
                        <p className={`text-2xl font-data ${theme.text.primary}`}>
                          ~{estimatedCost.processing_time_minutes} min
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Cost Confirmation */}
              {estimatedCost && (
                <div className="mt-6">
                  <CostConfirmation
                    costSGD={estimatedCost.estimated_cost.sgd}
                    onConfirmationChange={setCostConfirmed}
                  />
                </div>
              )}

              {/* Start failure — surfaced here so the user sees why nothing happened */}
              {startError && (
                <div
                  role="alert"
                  data-testid="start-error"
                  className="mt-6 p-4 bg-qc-error/10 rounded-lg border border-qc-error/30 flex items-start gap-3"
                >
                  <AlertCircle className="w-5 h-5 text-qc-error mt-0.5 shrink-0" />
                  <p className="text-sm text-qc-error">{startError}</p>
                </div>
              )}

              {/* Start Processing Button */}
              <button
                onClick={handleStartProcessing}
                disabled={starting || !selectedFileId || !estimatedCost || !costConfirmed || !!chunkSizeError || !!maxConcurrentError}
                className="w-full mt-6 py-4 px-8 text-lg bg-interactive-primary text-text-inverse rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="start-button"
              >
                {starting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Start Processing
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenericPromptWorkflow;

